import { useTranslation } from 'react-i18next'
import { EmptyState, SkeletonRows } from '@/components/forge/States'
import { useSession } from '@/features/auth/mutations'
import { holdsAny } from '@/features/auth/permissions'

/**
 * Gate for a screen whose API refuses everyone without one of `keys`.
 *
 * ── Navigation, not authorisation ───────────────────────────────────────
 * Like `RequireAuth`, this decides what to render. The API re-decides on every
 * request and its answer is the real one; this exists so that somebody arriving
 * by a pasted link reads one sentence about the permission they lack, rather than
 * a screen of disabled controls and a failed load.
 *
 * It renders the refusal IN PLACE rather than redirecting: a link that silently
 * lands somewhere else teaches nothing, and a sentence naming the permission can
 * be forwarded to the administrator who grants it.
 *
 * ── Only for screens that do not gate themselves ────────────────────────
 * Most live screens already render their own refusal (Logbook, Work Orders,
 * Users…), worded for that screen. This wraps the ones that did not, so the
 * copy below is per-screen too. Fails closed, as `usePermission` does — and the
 * session is seeded from storage, so the skeleton is a first sign-in only.
 */
export function RequirePermission({ keys, forbidden, children }) {
  const { t } = useTranslation('app')
  const session = useSession()

  if (!session.data) {
    return session.isPending ? (
      <div className="px-8 py-6 max-xl:px-5.5">
        <SkeletonRows count={4} />
      </div>
    ) : (
      <Refusal copy={copyFor(t, forbidden)} />
    )
  }

  if (holdsAny(session.data, keys)) return children
  return <Refusal copy={copyFor(t, forbidden)} />
}

function Refusal({ copy }) {
  return (
    <div className="px-8 py-6 max-xl:px-5.5">
      <EmptyState icon="lock" title={copy.title} body={copy.body} />
    </div>
  )
}

/* Literal keys, one screen each, so the i18n check can see every one of them. */
function copyFor(t, forbidden) {
  switch (forbidden) {
    case 'anomalies':
      return { title: t('anomalies.forbidden'), body: t('anomalies.forbiddenBody') }
    case 'assetClasses':
      return { title: t('settings.assetClasses.forbidden'), body: t('settings.assetClasses.forbiddenBody') }
    case 'hierarchy':
      return { title: t('settings.hierarchy.forbidden'), body: t('settings.hierarchy.forbiddenBody') }
    case 'users':
      return { title: t('settings.users.forbidden'), body: t('settings.users.forbiddenBody') }
    default:
      return { title: t('common:errors.forbidden'), body: '' }
  }
}
